import { useState, useEffect } from "react";
import { Trash, Minus, Plus, Package } from "@phosphor-icons/react";

const fmt = (v, c = "EUR") =>
  new Intl.NumberFormat("pt-PT", { style: "currency", currency: c || "EUR" }).format(v || 0);

/** Single row in the cart (product or pack). Pack rows don't allow quantity edits. */
export default function CartItemRow({ item, currency, isPack = false, disabled = false, onUpdate, onRemove }) {
  const initial = Number(item?.Quantidade) || 1;
  const [qty, setQty] = useState(String(initial));

  useEffect(() => { setQty(String(Number(item?.Quantidade) || 1)); }, [item?.Quantidade]);

  const commit = () => {
    const n = Math.max(1, Math.min(999, parseInt(qty, 10) || 1));
    setQty(String(n));
    if (n !== initial && onUpdate) onUpdate(n);
  };
  const bump = (delta) => {
    const n = Math.max(1, Math.min(999, (parseInt(qty, 10) || 1) + delta));
    setQty(String(n));
    if (onUpdate) onUpdate(n);
  };

  const name = item?.Descricao || "Produto";
  const subtotal = Number(item?.subtotal) || 0;

  return (
    <div data-testid={isPack ? `cart-pack-row-${item.ID_Pack}` : `cart-prod-row-${item.ID_Produto}`}
      className="bg-white rounded-xl border border-[#E5E7EB] p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4 hover:shadow-sm transition-shadow">
      <div className="w-12 h-12 shrink-0 rounded-lg bg-[#F3F4F6] flex items-center justify-center">
        <Package size={20} className="text-[#6B7280]" weight={isPack ? "fill" : "regular"} />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#0F1829] truncate">{name}</p>
        <p className="text-xs text-[#6B7280] mt-0.5">
          {isPack ? "Pack" : "Produto"}
          {item?.ID_Produto ? ` · #${item.ID_Produto}` : ""}
          {item?.ID_Pack ? ` · #${item.ID_Pack}` : ""}
        </p>
      </div>

      {/* Quantity controls — packs don't allow direct qty editing */}
      {!isPack ? (
        <div className="flex items-center gap-1 bg-[#F3F4F6] rounded-lg p-1">
          <button data-testid={`cart-qty-minus-${item.ID_Produto}`} type="button" disabled={disabled} onClick={() => bump(-1)}
            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-white transition-colors disabled:opacity-40"
            aria-label="Diminuir">
            <Minus size={14} />
          </button>
          <input type="number" inputMode="numeric" min="1" max="999" value={qty}
            onChange={(e) => setQty(e.target.value.replace(/[^0-9]/g, ""))}
            onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
            data-testid={`cart-qty-input-${item.ID_Produto}`}
            className="w-10 text-center text-sm font-medium bg-transparent outline-none" />
          <button data-testid={`cart-qty-plus-${item.ID_Produto}`} type="button" disabled={disabled} onClick={() => bump(1)}
            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-white transition-colors disabled:opacity-40"
            aria-label="Aumentar">
            <Plus size={14} />
          </button>
        </div>
      ) : (
        <div className="text-xs text-[#6B7280] px-2">Qtd. {item?.Quantidade ?? 1}</div>
      )}

      <div className="text-right min-w-[90px]">
        <p className="text-sm font-bold text-[#0F1829]">{fmt(subtotal, currency)}</p>
      </div>

      <button type="button" disabled={disabled} onClick={onRemove}
        data-testid={isPack ? `cart-pack-remove-${item.ID_Pack}` : `cart-prod-remove-${item.ID_Produto}`}
        className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg text-[#6B7280] hover:text-[#FF2B2B] hover:bg-red-50 transition-colors disabled:opacity-40"
        aria-label="Remover">
        <Trash size={16} />
      </button>
    </div>
  );
}
